import { Modal, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useHandlers, useSite } from '../../../../context/page-context';
import { type SettingPriceListState, useCustomerGroups } from '../../../customer-groups';
import { useCustomerGroupsApi } from '../../../customer-groups/hooks/use-customer-groups-api';
import { useAllPriceListsWithManualPriceCounts } from '../../hooks/use-price-lists';
import { PRICE_LIST_SOURCES } from '../../types';

const PREVENT_PRICE_LIST_MODAL_KEY = (siteId: string) => `${siteId}:sparkPreventPriceListModal`;

/**
 * Modal to assign a price list to customer groups.
 * Shown when:
 *  - The user has price lists
 *  - No price lists are assigned to any customer group
 * Either the first price list is assigned to the default customer group,
 * or the user is taken to the customer groups page to assign price lists.
 */
export function AssignPriceListModal() {
  const [show, setShow] = useState<boolean>(false);
  const site = useSite();

  const { navigationHandler, notificationHandler } = useHandlers();
  const { customerGroupsApi } = useCustomerGroupsApi();
  const { data: priceLists, isRefetching: priceListsRefetching } =
    useAllPriceListsWithManualPriceCounts();
  const { data: customerGroups, isRefetching: customerGroupsRefetching } = useCustomerGroups();

  const userPriceLists = useMemo(
    () => priceLists.filter((list) => list.source !== PRICE_LIST_SOURCES.PLATFORM),
    [priceLists],
  );

  const hasZeroOrOnePriceList = userPriceLists.length <= 1;
  const [loading, setLoading] = useState<boolean>(false);

  // Price lists can only be assigned if:
  //  - The user has customer groups
  //  - No customer group has a user-created price list assigned
  //  (platform price lists assigned by default on new installs are ignored)
  const canAssignPriceList = useMemo(() => {
    if (customerGroups.length === 0) {
      return false;
    }

    const userPriceListSlugs = new Set(userPriceLists.map((p) => p.slug));

    const hasExistingUserAssignment = customerGroups.some((group) => {
      const selection = group.settings.find((s) => s.key === 'priceListSelection');
      if (!selection?.value) return false;
      const assignments = selection.value as SettingPriceListState;
      return assignments.some((a) => userPriceListSlugs.has(a.price_list_slug));
    });

    return !hasExistingUserAssignment;
  }, [customerGroups, userPriceLists]);

  useEffect(() => {
    // If already clicked "I'll do this later", don't show again
    if (localStorage.getItem(PREVENT_PRICE_LIST_MODAL_KEY(site.siteId)) === 'true') {
      return;
    }

    // If loading in price lists or customer groups, wait until loaded
    if (priceListsRefetching || customerGroupsRefetching) {
      return;
    }

    // No price lists to assign
    if (!userPriceLists.length) {
      return;
    }

    // Can't assign price list
    if (!canAssignPriceList) {
      return;
    }

    // Show assign price list modal
    setShow(true);
  }, [
    canAssignPriceList,
    customerGroupsRefetching,
    userPriceLists,
    priceListsRefetching,
    site.siteId,
  ]);

  async function handleAssignFirstPriceList() {
    setLoading(true);

    // Can't assign if no default customer group or no price lists
    // This state should not be possible, but this is just in case
    const defaultCustomerGroup = customerGroups.find((group) => group.slug === 'base') ?? null;
    if (!defaultCustomerGroup) {
      console.error(
        "Failed to assign first price list to default group - default group doesn't exist",
      );
      setLoading(false);
      return;
    }

    if (!userPriceLists[0]) {
      console.error('Failed to assign first price list to default group - no price lists found');
      setLoading(false);
      return;
    }

    try {
      await customerGroupsApi
        .group(defaultCustomerGroup.slug)
        .settings()
        .update({ priceListSelection: [{ price_list_slug: userPriceLists[0].slug }] });

      localStorage.setItem(PREVENT_PRICE_LIST_MODAL_KEY(site.siteId), 'true');

      notificationHandler.success(
        t('price_lists.notifications.success.price_list_assigned_to_base'),
      );

      navigationHandler.navigate('/', {
        query: {
          expand: 'pricing',
          precheckPricing: '1',
        },
      });
    } catch {
      notificationHandler.error();
    } finally {
      setShow(false);
      setLoading(false);
    }
  }

  function handleAssignPriceList() {
    localStorage.setItem(PREVENT_PRICE_LIST_MODAL_KEY(site.siteId), 'true');
    navigationHandler.navigate('/customers/groups');
  }

  const modalName = hasZeroOrOnePriceList ? 'assign_first_price_list' : 'assign_price_list';
  const primaryAction = hasZeroOrOnePriceList ? handleAssignFirstPriceList : handleAssignPriceList;

  return (
    <Modal
      isOpen={show}
      onClose={() => setShow(false)}
      primaryAction={{
        onClick: primaryAction,
        text: t(`price_lists.modals.${modalName}.assign`),
        loading,
      }}
      secondaryAction={{
        text: t(`price_lists.modals.${modalName}.later`),
        onClick: () => {
          localStorage.setItem(PREVENT_PRICE_LIST_MODAL_KEY(site.siteId), 'true');
          setShow(false);
        },
      }}
    >
      <Modal.Header>{t(`price_lists.modals.${modalName}.title`)}</Modal.Header>
      <Modal.Body>
        <Text variant="secondary" size="md">
          {/* Trans component used here to support <br/> */}
          <Trans i18nKey={`price_lists.modals.${modalName}.body`} />
        </Text>
      </Modal.Body>
    </Modal>
  );
}
